import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import proNotifications from '../../../modules/proNotifications';
import AvatarIcon from '../../AvatarIcon';
import StateLink from '../../StateLink';

import './Notification.scss';
import './ClientBooking.scss';

/**
 * Client booking notification
 */
export default function ClientBooking({
  notification,
  hideTextBlock,
  classes,
  onClick,
}) {
  const localStart = moment(notification.local_start);
  const isNCD = notification.is_new_client_delivery;
  const isPaidNCD = notification.is_monetized_new_client_delivery;
  const isSmartPrice = notification.is_smart_price;
  const isUserFirstTimeBookingWithPro = notification.is_user_first_time_booking_with_pro;

  const showSmartPricing = !isNCD && isSmartPrice;

  let badgeAppearance;

  if (isNCD) {
    badgeAppearance = 'square punch';
  } else if (showSmartPricing) {
    badgeAppearance = 'square purple';
  }

  return (
    <StateLink
      to="provider.appointment"
      params={{ viewApptId: notification.object_id }}
      clickTracking={{
        name: proNotifications.NOTIFICATION_CLICKED_EVENT,
        params: proNotifications.createNotificationParams(notification),
      }}
      onClick={onClick}
    >
      <section className={classNames(
        'client-booking notification',
        {
          'is-ncd': isNCD,
          'is-smart-pricing': showSmartPricing,
        },
      )}
      >
        <div className="icon">
          <AvatarIcon
            icon={notification.client_thumbnail}
            name={notification.client_name}
            className={classes.avatar}
            sizeClass="small"
            badge={isUserFirstTimeBookingWithPro ? 'New!' : null}
            badgeAppearance={badgeAppearance}
          />
        </div>
        <section className="notification-body">
          <p className="notification-text">
            <strong>
              {notification.client_name} booked&nbsp;
              {notification.service_name}&nbsp;
            </strong>
            <span>
              for {localStart.format('dddd, MMM D')}
              &nbsp;at {localStart.format('h:mm A')}
            </span>
          </p>
          <p className="notification-details">
            {showSmartPricing && (
              <span className="notification-label">
                Smart Pricing
              </span>
            )}
            <span className="from-now">
              {proNotifications.getFromNowDisplay(notification.creation_time)}
            </span>
          </p>
          {(!hideTextBlock && isNCD) && (
            <p className="notification-subtext">
              {isPaidNCD
                ? 'We invested in marketing to help you get booked by new clients looking for long-term professionals.'
                : 'For a trial period only, we boosted your profile for free to help you get discovered by new clients.'}
            </p>
          )}
          {(!hideTextBlock && showSmartPricing) && (
            <p className="notification-subtext">
              This time slot is popular! We helped you book it for 20% more.
            </p>
          )}
        </section>
      </section>
    </StateLink>
  );
}

ClientBooking.propTypes = {
  /** The notification object */
  notification: PropTypes.object.isRequired,
  /** True to disable the linking functionality, otherwise false */
  hideTextBlock: PropTypes.bool,
  /** Supply class names to child components */
  classes: PropTypes.shape({
    /** For the avatar icon */
    avatar: PropTypes.string,
    /** For the subtext divider */
    divider: PropTypes.string,
  }),
  /** Fires when the notification is clicked */
  onClick: PropTypes.func,
};

ClientBooking.defaultProps = {
  hideTextBlock: false,
  classes: {
    avatar: null,
  },
  onClick: null,
};
